import { LinkFragmentFragment } from '../../graphql/gatsby'
import { logAndCaptureMessage } from '../../utils/errorTools'
import { getResourceAssetUrl } from '../library'
import { basePathForTypeName } from './path'

export type LinkFragment = LinkFragmentFragment
export type LinkFragmentTarget = NonNullable<NonNullable<LinkFragment['target']>[0]>
export type LinkFragmentTargetInternal = Extract<
  LinkFragmentTarget,
  { __typename?: 'SanityInternalLink' }
>
export type LinkFragmentTargetExternal = Extract<
  LinkFragmentTarget,
  { __typename?: 'SanityExternalLink' }
>
export type LinkFragmentTargetPath = Extract<LinkFragmentTarget, { __typename?: 'SanityPathLink' }>
export type LinkFragmentTargetLogin = Extract<
  LinkFragmentTarget,
  { __typename?: 'SanityLoginLink' }
>
export type LinkFragmentTargetLibrary = Extract<
  LinkFragmentTarget,
  { __typename?: 'SanityLibraryLink' }
>
export type LinkFragmentTargetRegistration = Extract<
  LinkFragmentTarget,
  { __typename?: 'SanityRegistrationLink' }
>
export type LinkFragmentTargetSupportRequest = Extract<
  LinkFragmentTarget,
  { __typename?: 'SanitySupportRequestLink' }
>

export interface NormalizedSanityLink {
  href?: string | null
  blank?: boolean
}

export const isSanityInternalLink = (
  linkDetails: LinkFragmentTarget,
): linkDetails is LinkFragmentTargetInternal => {
  return linkDetails._type === 'internalLink' || linkDetails.__typename === 'SanityInternalLink'
}

export const isSanityPathLink = (
  linkDetails: LinkFragmentTarget,
): linkDetails is LinkFragmentTargetPath => {
  return linkDetails._type === 'pathLink' || linkDetails.__typename === 'SanityPathLink'
}

export const isSanityExternalLink = (
  linkDetails: LinkFragmentTarget,
): linkDetails is LinkFragmentTargetExternal => {
  return linkDetails._type === 'externalLink' || linkDetails.__typename === 'SanityExternalLink'
}

export const isSanityLoginLink = (
  linkDetails: LinkFragmentTarget,
): linkDetails is LinkFragmentTargetLogin => {
  return linkDetails._type === 'loginLink' || linkDetails.__typename === 'SanityLoginLink'
}

export const isSanityLibraryLink = (
  linkDetails: LinkFragmentTarget,
): linkDetails is LinkFragmentTargetLibrary => {
  return linkDetails._type === 'libraryLink' || linkDetails.__typename === 'SanityLibraryLink'
}

export const isSanityRegistrationLink = (
  linkDetails: LinkFragmentTarget,
): linkDetails is LinkFragmentTargetRegistration => {
  return (
    linkDetails._type === 'registrationLink' || linkDetails.__typename === 'SanityRegistrationLink'
  )
}

export const isSanitySupportRequestLink = (
  linkDetails: LinkFragmentTarget,
): linkDetails is LinkFragmentTargetSupportRequest => {
  return (
    linkDetails._type === 'supportRequestLink' ||
    linkDetails.__typename === 'SanitySupportRequestLink'
  )
}

export const normalizeSanityInternalHref = ({
  href,
  typeName,
}: {
  href: string | null | undefined
  typeName: string | null | undefined
}): string | null | undefined => {
  if (typeof href === 'string') {
    return `${typeName ? basePathForTypeName(typeName) : ''}${
      href.startsWith('/') ? '' : '/'
    }${href}`
  }
  return href
}

export const normalizeSanityLink = ({
  link,
  frontPage,
}: {
  link: LinkFragment
  frontPage?: string | null | undefined
}): NormalizedSanityLink | undefined => {
  const { target } = link
  const [linkDetails] = target ?? []
  if (!linkDetails) {
    return undefined
  }
  if (isSanityLoginLink(linkDetails)) {
    return undefined
  }
  if (isSanityRegistrationLink(linkDetails)) {
    return undefined
  }
  if (isSanitySupportRequestLink(linkDetails)) {
    return undefined
  }
  if (isSanityInternalLink(linkDetails)) {
    const normalizedHref = normalizeSanityInternalHref({
      href: linkDetails.reference?.slug?.current,
      typeName: linkDetails.reference?._type,
    })
    return {
      href: frontPage && normalizedHref === frontPage ? '/' : normalizedHref,
    }
  }
  if (isSanityPathLink(linkDetails)) {
    return {
      href: frontPage && linkDetails.path === frontPage ? '/' : linkDetails.path,
    }
  }
  if (isSanityExternalLink(linkDetails)) {
    return {
      href: linkDetails.href,
      blank: linkDetails.blank || undefined,
    }
  }
  if (isSanityLibraryLink(linkDetails) && linkDetails.reference) {
    return {
      href: getResourceAssetUrl(linkDetails.reference),
    }
  }
  logAndCaptureMessage(`Could not normalize Sanity link: ${JSON.stringify(link)}`)
  return undefined
}
